import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import { useFeatures } from '../../hooks/useFeatures';
import styles from './MobileBottomNav.module.css';

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  badge?: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    label: '首頁',
    path: '/',
    icon: '🏠',
  },
  {
    id: 'ads',
    label: '廣告',
    path: '/ads',
    icon: '🛒',
  },
  {
    id: 'wish',
    label: '願望',
    path: '/wishes',
    icon: '⭐',
  },
  {
    id: 'products',
    label: '商品',
    path: '/products',
    icon: '🛍️',
  },
  {
    id: 'cart',
    label: '購物車',
    path: '/cart',
    icon: '🛒',
  },
  {
    id: 'profile',
    label: '會員',
    path: '/member',
    icon: '👤',
  },
];

interface MobileBottomNavProps {
  items?: NavItem[];
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ items = NAV_ITEMS }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount } = useNotifications();
  const features = useFeatures();
  const { showAdsLink, showWishesLink, showProductsLink, isCartEnabled } = features;

  const [itemsWithBadge, setItemsWithBadge] = useState<NavItem[]>(items);

  // [NEW v0.10.0] Update badge dynamically based on unread count
  useEffect(() => {
    // Filter items based on feature flags
    const filteredItems = items.filter(item => {
      if (item.id === 'ads') return showAdsLink;
      if (item.id === 'wish') return showWishesLink;
      if (item.id === 'products') return showProductsLink;
      if (item.id === 'cart') return isCartEnabled;
      return true; // Always show home and profile
    });

    const updated = filteredItems.map(item => {
      // Show badge on "profile" tab if there are unread notifications
      if (item.id === 'profile' && unreadCount > 0) {
        return {
          ...item,
          badge: unreadCount > 99 ? '99+' : String(unreadCount),
        };
      }
      return item;
    });

    setItemsWithBadge(updated);
  }, [unreadCount, items, showAdsLink, showWishesLink, showProductsLink, isCartEnabled]);

  const isActive = (path: string): boolean => {
    return location.pathname === path;
  };

  const handleNavClick = (item: NavItem) => {
    navigate(item.path);
  };

  return (
    <nav className={styles.mobileBottomNav}>
      <div className={styles.mobileBottomNav__container}>
        {itemsWithBadge.map((item) => (
          <button
            key={item.id}
            className={`${styles.mobileBottomNav__item} ${isActive(item.path) ? styles['mobileBottomNav__item--active'] : ''}`}
            onClick={() => handleNavClick(item)}
            aria-label={item.label}
            aria-current={isActive(item.path) ? 'page' : undefined}
          >
            <div className={styles.mobileBottomNav__iconWrapper}>
              <span className={styles.mobileBottomNav__icon}>{item.icon}</span>
              {item.badge && (
                <span className={styles.mobileBottomNav__badge}>{item.badge}</span>
              )}
            </div>
            <span className={styles.mobileBottomNav__label}>{item.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
